import * as React from 'react';
import { connect } from 'react-redux';
import * as classnames from 'classnames';
import * as _ from 'lodash';
import Select from 'react-select';
import { TEXT_REFERENCE_QTYPES } from '../../../../constants/question';
import IncaVariableSelect from '../IncaVariableSelect';
import IncaVariableReference from '@nexxt/common/incaLogic/IncaVariableReference';
import {
    INCA_VARIABLE_FORMATTER_OPTION_TYPES,
    INCA_VARIABLE_FORMATTER_TYPES,
    INCA_VARIABLE_SUPPORTED_FORMATTER_OPTIONS,
    INCA_VARIABLE_TYPES
} from '@nexxt/common/incaLogic/types';
import { MULTIMEDIA_TYPES, QTYPES, Question } from '@nexxt/common/types';
import { EditorState, Modifier, SelectionState } from 'draft-js';
import Modal, {
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle
} from '../../modal';
import { getQuestionByQuestionId } from '../../../../services/question.service';
import { getEntityRange } from 'draftjs-utils';
import { URL_METADATA_QUESTION_VALUE } from '@nexxt/common/constants';
import { Notes } from '../../notes';
import { ALERT_MESSAGE_TYPES } from '../../../../interface/project.interface';

interface IncaVariableReferenceEditorModalProps {
    showAnswerReferenceModal: boolean;
    answerReferenceQuestionValue: string;
    editorState?: EditorState;
    selectedEntity?: { offsetKey: string; entityKey: string };
    disabled: boolean;
    langName?: string;
    formattingOptions?: {
        [key: string]: INCA_VARIABLE_SUPPORTED_FORMATTER_OPTIONS;
    };
    toggleShowAnswerReferenceModal: (toggle: boolean) => void;
    onEditorChange?: (editorState: EditorState) => void;
    addNewBubble?: (type: MULTIMEDIA_TYPES, data?: any) => void;
    deleteEntity?: (entityKey: string, offsetKey: string) => void;

    // from mediaBubble
    handleImageReference?: (formattingOptions) => void;
    isMediaBubble?: boolean;

    getQuestionById: (questionId: number, options: any) => Promise<Question>;
}

const IncaVariableReferenceEditorModal: React.FC<IncaVariableReferenceEditorModalProps> =
    (props: IncaVariableReferenceEditorModalProps) => {
        const [incaVariableReference, setIncaVariableReference] =
            React.useState<IncaVariableReference>(null);
        const [selectedQuestion, setSelectedQuestion] = React.useState(null);
        const [formattingOptions, setFormattingOptions] = React.useState({});
        const [urlKey, setUrlKey] = React.useState('');
        const incaVariable = incaVariableReference?.getIncaVariable();

        React.useEffect(() => {
            if (selectedQuestion) {
                getQuestionDetail();
                // setUrlKey('');
            }
        }, [selectedQuestion]);

        React.useEffect(() => {
            if (props.selectedEntity?.entityKey) {
                const content = props.editorState.getCurrentContent();
                const entity = content
                    .getEntity(props.selectedEntity?.entityKey)
                    .getData();
                setFormattingOptions(entity?.formattingOptions || {});
                setUrlKey(entity?.urlKey);
            }
        }, [props.selectedEntity?.entityKey]);

        React.useEffect(() => {
            if (props.formattingOptions) {
                setFormattingOptions(props.formattingOptions);
            }
        }, []);

        const getQuestionDetail = async () => {
            let q = selectedQuestion;
            if (selectedQuestion.id > 0) {
                q = await props.getQuestionById(selectedQuestion.id, {
                    logicList: true
                });
            }

            const incaVariableReference = new IncaVariableReference(q);
            setIncaVariableReference(incaVariableReference);
            console.log('formatting option updated from default');

            _.isEmpty(formattingOptions) &&
                setFormattingOptions(
                    incaVariableReference.getDefaultFormattingOptions(
                        props.langName
                    )
                );
        };

        const handleFormattingSelection = (type, selectedFormatter) => {
            setFormattingOptions((prevFormattingOptions) => ({
                ...prevFormattingOptions,
                [type]: {
                    label: selectedFormatter.label,
                    value: selectedFormatter.value
                }
            }));
        };

        const addDynamicTextRef = () => {
            const { editorState, onEditorChange, selectedEntity } = props;

            const referenceQuestion =
                selectedQuestion?.value === URL_METADATA_QUESTION_VALUE
                    ? urlKey
                    : `${selectedQuestion.label}`;

            if (
                formattingOptions[INCA_VARIABLE_FORMATTER_TYPES.TYPE_FORMAT]
                    ?.value ===
                INCA_VARIABLE_FORMATTER_OPTION_TYPES.TYPE_FORMAT_IMAGE
            ) {
                props.addNewBubble(MULTIMEDIA_TYPES.IMAGE, {
                    answerReference: { selectedQuestion, formattingOptions }
                });

                selectedEntity?.entityKey &&
                    props.deleteEntity(
                        selectedEntity.entityKey,
                        selectedEntity.offsetKey
                    );
            } else {
                const type = incaVariable.type;
                if (selectedEntity?.entityKey) {
                    const contentState = editorState.getCurrentContent();

                    const { start, end } = getEntityRange(
                        editorState,
                        selectedEntity?.entityKey
                    );
                    const blockKey = selectedEntity?.offsetKey.split('-')[0];
                    const newSelection = SelectionState.createEmpty(
                        blockKey
                    ).merge({
                        focusOffset: end,
                        anchorOffset: start
                    });

                    const contentStateWithNewText = Modifier.replaceText(
                        contentState,
                        newSelection,
                        referenceQuestion,
                        null,
                        selectedEntity?.entityKey
                    );
                    const newContentState =
                        contentStateWithNewText.replaceEntityData(
                            selectedEntity?.entityKey,
                            {
                                referenceQuestion,
                                option: selectedQuestion,
                                formattingOptions,
                                type,
                                urlKey
                            }
                        );

                    onEditorChange(
                        EditorState.push(
                            editorState,
                            newContentState,
                            'apply-entity'
                        )
                    );
                } else {
                    const entityKey = editorState
                        .getCurrentContent()
                        .createEntity(
                            'DYNAMIC_TEXT_REFERENCE',
                            'IMMUTABLE',
                            {
                                referenceQuestion,
                                option: selectedQuestion,
                                formattingOptions,
                                type,
                                urlKey
                            } // need to reduce size of option here.
                        )
                        .getLastCreatedEntityKey();

                    const newContentState = Modifier.replaceText(
                        editorState.getCurrentContent(),
                        editorState.getSelection(),
                        referenceQuestion,
                        null,
                        entityKey
                    );
                    onEditorChange(
                        EditorState.push(
                            editorState,
                            newContentState,
                            'apply-entity'
                        )
                    );
                }
            }

            props.toggleShowAnswerReferenceModal(false);
        };

        const supportedFormatterTypes =
            incaVariableReference?.getFilteredSupportedFormatterTypes(
                props.langName
            ) || [];
        const hasWhiteSpace = /\s/.test(urlKey);
        return (
            <Modal
                isOpen={props.showAnswerReferenceModal}
                className={classnames('modal-dialog--pipe-options')}
            >
                <ModalHeader
                    onClose={() => props.toggleShowAnswerReferenceModal(false)}
                >
                    <ModalTitle>Dynamic Text Referencing</ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <form className="form" onSubmit={(e) => e.preventDefault()}>
                        <div className="form-group option-height">
                            <label className="form-group__label form-group__label--block">
                                Use the respondent&apos;s answer from this
                                question as options
                            </label>
                            <IncaVariableSelect
                                availableQTypes={TEXT_REFERENCE_QTYPES}
                                includeCurrentQuestion={false}
                                handleQuestionSelect={setSelectedQuestion}
                                selectedQuestionValue={
                                    props.answerReferenceQuestionValue
                                }
                                selectedQuestion={selectedQuestion}
                                disabled={props.disabled || props.isMediaBubble}
                            />
                            {incaVariable?.type ===
                                INCA_VARIABLE_TYPES.URL_METADATA && (
                                <>
                                    <label className="form-group__label form-group__label--block">
                                        Please enter the key you would like to
                                        reference from the URL sent to
                                        respondents
                                    </label>
                                    <input
                                        className="form-group__control answer--reference--url-parameter"
                                        type="text"
                                        value={urlKey}
                                        onChange={(e) =>
                                            setUrlKey(e.target.value)
                                        }
                                    />

                                    {hasWhiteSpace ? (
                                        <Notes type={ALERT_MESSAGE_TYPES.WARN}>
                                            <span>
                                                URL Metadata shouldn&apos;t
                                                contain whitespace. Please
                                                modify it or click{' '}
                                                <span
                                                    style={{
                                                        color: '#2d67cb',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() =>
                                                        setUrlKey(
                                                            _.snakeCase(urlKey)
                                                        )
                                                    }
                                                >
                                                    <b>here</b>
                                                </span>{' '}
                                                to replace it to{' '}
                                                <b>{_.snakeCase(urlKey)}</b>
                                            </span>
                                        </Notes>
                                    ) : null}
                                </>
                            )}

                            {supportedFormatterTypes?.length ? (
                                <label
                                    className="form-group__label form-group__label--block"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Answer Referencing Options:
                                </label>
                            ) : null}
                            <div className="answer--reference--options">
                                {supportedFormatterTypes?.map(
                                    (
                                        formatterType: INCA_VARIABLE_FORMATTER_TYPES
                                    ) => {
                                        const incaVariableFormatter =
                                            incaVariableReference.getFormatterByFormatterType(
                                                formatterType
                                            );
                                        const options =
                                            incaVariableFormatter.supportedFormatterOptions.map(
                                                (formatter) => ({
                                                    ...formatter,
                                                    isDisabled:
                                                        formatter.disabled
                                                })
                                            );
                                        const isImageOption =
                                            formattingOptions?.[
                                                INCA_VARIABLE_FORMATTER_TYPES
                                                    .TYPE_FORMAT
                                            ]?.value ===
                                            INCA_VARIABLE_FORMATTER_OPTION_TYPES.TYPE_FORMAT_IMAGE;
                                        const rankEnabled =
                                            selectedQuestion.type ===
                                                QTYPES.RANK_ORDER &&
                                            formatterType ===
                                                INCA_VARIABLE_FORMATTER_TYPES.MULTI_OPTION_DISPLAY &&
                                            formattingOptions[formatterType]
                                                ?.value ===
                                                INCA_VARIABLE_FORMATTER_OPTION_TYPES.MULTI_OPTION_DISPLAY_RANK;
                                        const currentValue =
                                            formattingOptions[
                                                incaVariableFormatter.value
                                            ] ||
                                            options.filter(
                                                (opt) => !opt?.disabled
                                            )?.[0];
                                        return (
                                            <div
                                                key={formatterType}
                                                className="answer--reference--option"
                                            >
                                                <span>
                                                    {
                                                        incaVariableFormatter.label
                                                    }
                                                </span>
                                                <div className="answer--reference--option-wrapper">
                                                    <Select
                                                        className={classnames(
                                                            'react-select react-select--inline',
                                                            {
                                                                'rank--enabled':
                                                                    rankEnabled
                                                            }
                                                        )}
                                                        label={'name'}
                                                        value={currentValue}
                                                        options={options}
                                                        onChange={(value) =>
                                                            handleFormattingSelection(
                                                                formatterType,
                                                                value
                                                            )
                                                        }
                                                        isDisabled={
                                                            (isImageOption &&
                                                                ![
                                                                    INCA_VARIABLE_FORMATTER_TYPES.TYPE_FORMAT,
                                                                    INCA_VARIABLE_FORMATTER_TYPES.MULTI_OPTION_DISPLAY
                                                                ].includes(
                                                                    formatterType
                                                                )) ||
                                                            (props.isMediaBubble &&
                                                                ![
                                                                    INCA_VARIABLE_FORMATTER_TYPES.MULTI_OPTION_DISPLAY
                                                                ].includes(
                                                                    formatterType
                                                                )) ||
                                                            props.disabled
                                                        }
                                                        styles={{
                                                            menuPortal: (
                                                                base
                                                            ) => ({
                                                                ...base,
                                                                zIndex: 9999
                                                            })
                                                        }}
                                                        menuPortalTarget={
                                                            document.body
                                                        }
                                                    />
                                                    {rankEnabled && (
                                                        <input
                                                            className="answer--reference--rank-input"
                                                            type="number"
                                                            value={
                                                                formattingOptions[
                                                                    incaVariableFormatter
                                                                        .value
                                                                ]?.rank
                                                            }
                                                            disabled={
                                                                props.disabled
                                                            }
                                                            min={1}
                                                            defaultValue={1}
                                                            onChange={(e) =>
                                                                setFormattingOptions(
                                                                    {
                                                                        ...formattingOptions,
                                                                        [incaVariableFormatter.value]:
                                                                            {
                                                                                ...formattingOptions[
                                                                                    incaVariableFormatter
                                                                                        .value
                                                                                ],
                                                                                rank: e
                                                                                    .target
                                                                                    .value
                                                                            }
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                            {formattingOptions[
                                INCA_VARIABLE_FORMATTER_TYPES.TYPE_FORMAT
                            ]?.value ===
                                INCA_VARIABLE_FORMATTER_OPTION_TYPES.TYPE_FORMAT_IMAGE &&
                                !props.isMediaBubble && (
                                    <div>
                                        Note: Image type references will be
                                        added as a new speech bubble
                                    </div>
                                )}
                        </div>
                    </form>
                </ModalBody>
                <ModalFooter>
                    <button
                        onClick={() =>
                            props.toggleShowAnswerReferenceModal(false)
                        }
                        className="btn btn--sm btn--transparent m-r-10"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() =>
                            props.isMediaBubble
                                ? props.handleImageReference(formattingOptions)
                                : addDynamicTextRef()
                        }
                        className="btn btn--sm btn--green"
                        disabled={hasWhiteSpace || props.disabled}
                    >
                        Done
                    </button>
                </ModalFooter>
            </Modal>
        );
    };

const mapDispatchToProps = (dispatch) => ({
    getQuestionById: (questionId: number, options, logic) =>
        dispatch(getQuestionByQuestionId(questionId, options))
});

/**
 * This component provides UI for researcher to create an IncaVariableReference.
 */
export default connect(
    null,
    mapDispatchToProps
)(IncaVariableReferenceEditorModal);
